import React, { useState } from 'react';
import { EyeIcon, EyeSlashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { authApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { inputClass, labelClass } from '../components/ui/formClasses';

export function LoginScreen() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const setAuth = useAuthStore((s) => s.setAuth);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'register') {
        if (!username.trim()) {
          throw new Error('Username is required');
        }
        if (!email.trim()) {
          throw new Error('Email is required');
        }
        if (password.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }
        await authApi.register({
          email: email.trim(),
          username: username.trim(),
          password,
          first_name: firstName.trim() || undefined,
          last_name: lastName.trim() || undefined,
        });
        setMode('login');
        setUsername(email.trim());
        setPassword('');
        setConfirmPassword('');
        setError('Account created. If email verification is enabled, verify your email, then sign in.');
        return;
      }
      const credential = username.trim();
      const login = await authApi.login({ username: credential, email: credential, password });
      try {
        const profile = await authApi.profile();
        setAuth(profile.data, login.data.access_token, login.data.refresh_token);
      } catch {
        setAuth({ id: 'current', username: credential, email: credential }, login.data.access_token, login.data.refresh_token);
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map((item: any) => item.msg).join(', '));
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError(err.message || 'Authentication failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-16">
        <div className="text-center">
          <h1 className="text-5xl font-bold text-green-800 dark:text-green-300 mb-4">Vendor Review</h1>
          <p className="text-xl text-green-600 dark:text-green-400 mb-8">Reusable Evaluation Matrices Made Simple</p>
          <div className="flex justify-center gap-4 mb-16">
            <button onClick={() => { setMode('login'); setShowAuthModal(true); setError(''); }} className="px-8 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors">Sign In</button>
            <button onClick={() => { setMode('register'); setShowAuthModal(true); setError(''); }} className="px-8 py-3 bg-white text-green-600 border-2 border-green-600 rounded-lg font-semibold hover:bg-green-50 transition-colors">Get Started</button>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
              <div className="text-4xl mb-4">📋</div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">Define Criteria</h3>
              <p className="text-gray-600 dark:text-gray-400">Create, import, and reuse decision criteria</p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
              <div className="text-4xl mb-4">🏢</div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">Compare Vendors</h3>
              <p className="text-gray-600 dark:text-gray-400">Track products, tools, and platforms side by side</p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">Export Matrices</h3>
              <p className="text-gray-600 dark:text-gray-400">Download formatted spreadsheet results</p>
            </div>
          </div>
        </div>
      </div>

      {showAuthModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/25 backdrop-blur-sm" onClick={() => setShowAuthModal(false)} />
          <div className="relative w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-medium text-gray-900 dark:text-white">{mode === 'login' ? 'Sign In' : 'Create Account'}</h3>
              <button onClick={() => setShowAuthModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <XMarkIcon className="w-7 h-7" />
              </button>
            </div>

            <form onSubmit={submit} className="space-y-4">
              {mode === 'register' && (
                <>
                  <div>
                    <label className={labelClass}>Username</label>
                    <input className={inputClass} value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Choose a username" required />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>First Name</label>
                      <input className={inputClass} value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" />
                    </div>
                    <div>
                      <label className={labelClass}>Last Name</label>
                      <input className={inputClass} value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Email</label>
                    <input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" required />
                  </div>
                </>
              )}

              {mode === 'login' && (
                <div>
                  <label className={labelClass}>Email or Username</label>
                  <input className={inputClass} type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Enter email or username" required />
                </div>
              )}

              <div>
                <label className={labelClass}>Password</label>
                <div className="relative">
                  <input className={`${inputClass} pr-10`} type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === 'login' ? 'Enter password' : 'Choose a password'} required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                    {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {mode === 'register' && (
                <div>
                  <label className={labelClass}>Confirm Password</label>
                  <input className={inputClass} type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm your password" required />
                </div>
              )}

              {error && <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">{error}</div>}

              <div className={mode === 'register' ? 'flex space-x-3 pt-2' : ''}>
                {mode === 'register' && <button type="button" onClick={() => setShowAuthModal(false)} className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors">Cancel</button>}
                <button disabled={loading} className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors">
                  {loading ? (mode === 'login' ? 'Signing in...' : 'Creating...') : mode === 'login' ? 'Sign In' : 'Create Account'}
                </button>
              </div>

              <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
                <button type="button" onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }} className="text-green-600 hover:underline font-medium">
                  {mode === 'login' ? 'Sign Up' : 'Sign In'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
